<template>
  <div class="q-ma-md">
    <div class="row">
      <div
        v-if="currentUnitPlacement"
        class="text-h5 q-pa-md col-10"
      >
        Update {{ currentUnitPlacement.complexName }},
        {{ currentUnitPlacement.unitName }}
      </div>
      <div
        v-else
        align="center"
        class="text-h5 q-pa-md col-10"
      >
        Add New Placement
      </div>
      <q-space />
      <q-icon
        name="help_outline"
        class="q-pa-md"
        size="sm"
      >
        <q-tooltip>
          <div class="text-body2">
            Unassigned: Placement has not been finalized <br />
            Placed: Algorithm has placed student <br />
            Blocked: Room is excluded from placement algorithm <br />
            and no manual placement is allowed (used for DSS, Buyouts, etc.) <br />
            Locked: Room is excluded from placement algorithm <br />
            Trait: Indicate if room is DSS or Buyout <br />
            ESA: Emotional Support Animal - Check if student has an approved ESA
          </div>
        </q-tooltip>
      </q-icon>
    </div>
    <div
      class="flex flex-center"
      padding
    >
      <q-form
        class="q-px-lg"
        ref="validateForm"
        @submit="onSubmit"
        style="width: 1100px"
        greedy
      >
        <q-select
          v-if="!currentUnitPlacement"
          v-model="placementDetails.complexName"
          :options="[]"
          label="Complex"
        />
        <q-select
          v-if="!currentUnitPlacement"
          v-model="placementDetails.unitName"
          :options="[]"
          label="Unit"
        />
        <div v-if="placementDetails.unitName">
          <div class="row q-ma-sm">
            <q-select
              class="col-4"
              v-model="placementDetails.raName"
              :options="raNameOptions"
              label="RA"
              use-input
              hide-selected
              fill-input
              input-debounce="0"
              @filter="raNameFilter"
              @update:model-value="setRaIdAndName(placementDetails.raName)"
              dense
            />
          </div>
          <div
            v-for="(bedRoom, count) in placementDetails.placementModels"
            class="row q-ma-sm"
            :key="bedRoom.placementId"
          >
            <q-field
              v-if="bedRoom.roomModel && !isRepeatingRoomType(bedRoom.roomModel.roomType, count)"
              class="col-12 q-mr-md"
              borderless
              stack-label
              dense
            >
              <template v-slot:control>
                <div class="text-weight-bold row">
                  {{ bedRoom.roomModel.roomType }}:</div>
              </template>
            </q-field>
            <div class="col-12">
              <div class="row">
                <q-select
                  class="col-2 q-mr-sm"
                  v-model="bedRoom.fullName"
                  :options="studentNameOptions"
                  label="Student Name"
                  use-input
                  hide-selected
                  fill-input
                  input-debounce="0"
                  @filter="studentNameFilter"
                  @update:model-value="setUserIdGivenName(bedRoom); validateAll()"
                  dense
                  :disable="bedRoom.placementStatus === 'Blocked'"
                  :readonly="bedRoom.placementStatus === 'Placed'"
                  :borderless="bedRoom.placementStatus === 'Placed'
                    || bedRoom.placementStatus === 'Blocked'"
                  :hide-dropdown-icon="bedRoom.placementStatus === 'Placed'
                    || bedRoom.placementStatus === 'Blocked'"
                  clearable
                  :rules="[checkIsDuplicatePlacement]"
                />
                <q-select
                  v-model="bedRoom.roomRateTable"
                  class="col-4 q-mr-sm"
                  :options="roomRateTableOptions"
                  use-input
                  hide-selected
                  fill-input
                  input-debounce="0"
                  @filter="roomRateTableFilter"
                  label="Room Rate Table"
                  dense
                  :disable="bedRoom.placementStatus === 'Blocked'"
                  :readonly="bedRoom.placementStatus === 'Placed'"
                  :borderless="bedRoom.placementStatus === 'Placed'
                    || bedRoom.placementStatus === 'Blocked'"
                  :hide-dropdown-icon="bedRoom.placementStatus === 'Placed'
                    || bedRoom.placementStatus === 'Blocked'"
                />
                <q-select
                  class="col-1 q-mr-sm"
                  :modelValue="bedRoom.placementStatus"
                  :options="['Unassigned', 'Tentative', 'Placed', 'Blocked']"
                  label="Status"
                  dense
                  @update:model-value="displayStateReassignmentMessage(bedRoom, $event)"
                  :rules="[(val) => checkIsValidPlacement(val, bedRoom)]"
                />
                <q-select
                  class="col-2 q-mr-sm"
                  v-model="bedRoom.trait"
                  :options="['None', 'Buyout', 'Dss', 'EmergencyUse', 'FacilitiesIssue',
                    'OccupiedConcurrentSession', 'PendingOnboardingOffboarding',
                    'RA', 'Sponsorship']"
                  label="Trait"
                  dense
                >
                  <template v-slot:selected>
                    <span class="ellipsis">{{ bedRoom.trait }}</span>
                  </template>
                </q-select>
                <q-checkbox
                  class="col q-mr-md"
                  size="xs"
                  v-model="bedRoom.isLocked"
                  label="Locked"
                  dense
                />
                <q-checkbox
                  class="col q-mr-md"
                  size="xs"
                  v-model="bedRoom.isSendingPlacementEmail"
                  label="Send Email"
                  dense
                />
                <q-checkbox
                  class="col"
                  size="xs"
                  v-model="bedRoom.animalInPlacement"
                  label="ESA"
                  dense
                >
                  <q-tooltip>
                    Emotional Support Animal - Check if student has an approved ESA
                  </q-tooltip>
                </q-checkbox>
              </div>
            </div>
          </div>
        </div>
      </q-form>
      <q-dialog
        v-model="isMovingFromPlacementModal"
        persistent
      >
        <q-card>
          <q-card-section class="row items-center">
            <q-avatar
              icon="warning"
              color="primary"
              text-color="white"
            />
            <span class="q-ml-sm">
              You are moving a placed student out of placement, please confirm.
            </span>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              flat
              label="Cancel"
              color="primary"
              v-close-popup
            />
            <q-btn
              flat
              label="Confirm"
              v-close-popup
              @click="selectedBedRoom.placementStatus = selectedPlacementStatus;
                      ifBlockedClearUserAndRateFields(
                        selectedBedRoom, selectedPlacementStatus)"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
      <q-dialog
        ref="placementConfirmationDialog"
        v-model="isPlacementConfirmationDialog"
        persistent
      >
        <q-card>
          <q-card-section class="row items-center">
            <q-avatar
              icon="warning"
              color="primary"
              text-color="white"
            />
            <span class="q-ml-sm">
              This will Place the student(s) and send placement emails, please confirm.
            </span>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              flat
              label="Cancel"
              color="primary"
              v-close-popup
            />
            <q-btn
              flat
              label="Confirm"
              @click="confirmPlacementsAndSubmit()"
              v-close-popup="hasConfirmedPlacements ? 2 : 0"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, ref } from 'vue';
import { useQuasar } from 'quasar';
import useComplex from '@/hooks/complex';
import useUtility from '@/hooks/utility';
import useUiUtilities from '@/utilities/uiUtilities';
import useUiValidations from '@/utilities/uiValidations';
import usePlacement from '@/hooks/placement';
import useUser from '@/hooks/user';

export default {
  props: {
    sessionId: Number,
  },
  async setup(props) {
    const $q = useQuasar();
    const {
      complexDetails,
      currentComplexId,
    } = useComplex();
    const {
      getRoomRateTables,
      roomRateTables,
    } = useUtility();
    const {
      checkInput,
      checkSelect,
      checkIsValidComplexCode,
      checkIsValidUsaPhone,
    } = useUiValidations();
    const { notifyResult, statesOptions } = useUiUtilities();
    const {
      currentUnitPlacement,
      fetchPlacementsBySession,
      pagination,
      placementDetails,
      studentNameList,
      updateUnitPlacment,
      updateStudentNamelist,
    } = usePlacement();
    const { getRaNames, getUserNameIds, raNameList } = useUser();

    const prevUnitPlacement = ref(null);
    const placementConfirmationDialog = ref(null);

    onBeforeUnmount(async () => {
      await updateStudentNamelist(props.sessionId);
    });

    await getRaNames(props.sessionId);

    if (currentUnitPlacement) {
      placementDetails.value = currentUnitPlacement.value;
      prevUnitPlacement.value = JSON.parse(JSON.stringify(currentUnitPlacement.value));
    }

    const confirmEmail = ref();
    const validateForm = ref(null);
    const applicantList = ref([]);
    const selectedBedRoom = ref(null);
    const selectedPlacementStatus = ref(null);
    const isPlacementConfirmationDialog = ref(null);
    const hasConfirmedPlacements = ref(false);

    const checkForNewPlacement = () => {
      const prevPlacements = prevUnitPlacement.value.placementModels;
      const newPlacements = placementDetails.value.placementModels;

      if (newPlacements.length !== prevPlacements.length) {
        console.log('new and old placements do not match in length');
        return -1;
      }

      let hasNewPlacement = false;
      for (let i = 0; i < prevPlacements.length; i += 1) {
        if (newPlacements[i].placementStatus === 'Placed' && prevPlacements[i].placementStatus !== 'Placed') {
          hasNewPlacement = true;
          break;
        }
      }
      isPlacementConfirmationDialog.value = hasNewPlacement;
      hasConfirmedPlacements.value = !hasNewPlacement;

      return hasNewPlacement;
    };

    const onRaUpdate = async () => {
      await fetchPlacementsBySession(
        pagination.value.page, pagination.value.rowsPerPage,
        pagination.value.descending, pagination.value.sortBy,
        placementDetails.value,
      );
    };

    const onSubmit = async () => {
      const validationResult = await validateForm.value.validate();
      if (validationResult) {
        if (!hasConfirmedPlacements.value && checkForNewPlacement()) {
          return -1;
        }
        $q.loading.show();
        const response = await updateUnitPlacment(placementDetails.value);
        notifyResult(response.status, response);
        if (response.status === 200 || response.status === 201) {
          if (prevUnitPlacement.value.raId !== placementDetails.value.raId) {
            await onRaUpdate();
          }
        }
        $q.loading.hide();

        return response;
      }

      return -1;
    };

    const setRaIdAndName = (ra) => {
      placementDetails.value.raId = ra.value;
      placementDetails.value.raName = ra.label; // eslint-disable-line no-param-reassign
    };

    const setUserIdGivenName = (bedroom) => {
      if (bedroom.fullName === null) { // only null when clear button is pressed
        bedroom.userId = null; // eslint-disable-line no-param-reassign

        return;
      }
      bedroom.userId = bedroom.fullName.value; // eslint-disable-line no-param-reassign
      bedroom.fullName = bedroom.fullName.label; // eslint-disable-line no-param-reassign
    };

    let prevRoomtype = '';
    const isRepeatingRoomType = (roomType, count) => {
      let result = false;
      if (prevRoomtype === roomType) {
        result = true;
      }
      prevRoomtype = roomType;
      if (count + 1 === placementDetails?.value?.placementModels?.length) {
        prevRoomtype = '';
      }

      return result;
    };

    applicantList.value = await getUserNameIds(props.sessionId);

    await updateStudentNamelist(props.sessionId);
    const addCurrentToUnPlacedList = () => {
      for (let i = 0; i < placementDetails?.value?.placementModels?.length; i += 1) {
        const occupant = placementDetails.value.placementModels[i]?.fullName;
        const userId = placementDetails.value.placementModels[i]?.userId;
        if (occupant && userId) {
          studentNameList.value.push({ value: userId, label: occupant });
        }
      }
    };
    addCurrentToUnPlacedList();

    await getRoomRateTables();

    const studentNameOptions = ref(studentNameList.value);
    const raNameOptions = ref(raNameList.value);
    const roomRateTableList = roomRateTables;
    const roomRateTableOptions = ref(roomRateTableList.value);

    const ifBlockedClearUserAndRateFields = (bedRoom, status) => {
      if (status === 'Blocked') {
        bedRoom.userId = null; // eslint-disable-line no-param-reassign
        bedRoom.fullName = null; // eslint-disable-line no-param-reassign
        bedRoom.username = null; // eslint-disable-line no-param-reassign
        bedRoom.roomRateTable = ''; // eslint-disable-line no-param-reassign

        return true;
      }

      return false;
    };

    const isMovingFromPlacementModal = ref(false);
    const displayStateReassignmentMessage = (bedRoom, status) => {
      selectedBedRoom.value = bedRoom;
      selectedPlacementStatus.value = status;
      if (bedRoom.placementStatus === 'Placed') {
        isMovingFromPlacementModal.value = true;
      } else {
        ifBlockedClearUserAndRateFields(bedRoom, status);
        bedRoom.placementStatus = status; // eslint-disable-line no-param-reassign
      }
    };

    const confirmPlacementsAndSubmit = async () => {
      hasConfirmedPlacements.value = true;
      await onSubmit();
    };

    const validateAll = async () => {
      await validateForm.value.validate();
    };

    const checkIsDuplicatePlacement = (val) => {
      if (val === null) {
        return true;
      }
      let result = 0;
      for (let i = 0; i < placementDetails?.value?.placementModels?.length; i += 1) {
        const occupant = placementDetails.value.placementModels[i].fullName;
        if (occupant === val) {
          result += 1;
        }
      }
      return result === 1 || 'Cannot place same person in multiple placements.';
    };

    const checkIsValidPlacement = (val, bedRoom) => {
      if (val === 'Unassigned' || val === 'Blocked') {
        return true;
      }

      return (bedRoom.fullName !== null && bedRoom.roomRateTable !== null)
        || 'Name and room rate table cannot be empty for placement';
    };

    const resetToOriginalState = () => {
      if (prevUnitPlacement.value) {
        placementDetails.value = JSON.parse(JSON.stringify(prevUnitPlacement.value));

        if (validateForm.value) {
          validateForm.value.resetValidation();
        }
      }
    };

    return {
      checkForNewPlacement,
      checkInput,
      checkSelect,
      checkIsDuplicatePlacement,
      checkIsValidComplexCode,
      checkIsValidPlacement,
      checkIsValidUsaPhone,
      hasConfirmedPlacements,
      confirmEmail,
      confirmPlacementsAndSubmit,
      currentComplexId,
      currentUnitPlacement,
      complexDetails,
      displayStateReassignmentMessage,
      ifBlockedClearUserAndRateFields,
      isPlacementConfirmationDialog,
      isRepeatingRoomType,
      isMovingFromPlacementModal,
      onSubmit,
      placementConfirmationDialog,
      placementDetails,
      raNameOptions,
      resetToOriginalState,
      roomRateTableList,
      roomRateTableOptions,
      selectedBedRoom,
      selectedPlacementStatus,
      statesOptions,
      studentNameList,
      studentNameOptions,
      setRaIdAndName,
      setUserIdGivenName,
      validateAll,
      validateForm,
      bedRoomTest: ['D-211M1', 'D-211N1', 'D-211N2'],

      studentNameFilter(val, update) {
        update(() => {
          const needle = val.toLowerCase();
          studentNameOptions.value = studentNameList.value.filter(
            (v) => v.label.toLowerCase().indexOf(needle) > -1,
          );
        });
      },

      raNameFilter(val, update) {
        update(() => {
          const needle = val.toLowerCase();
          raNameOptions.value = raNameList.value.filter(
            (v) => v.label.toLowerCase().indexOf(needle) > -1,
          );
        });
      },

      roomRateTableFilter(val, update) {
        update(() => {
          const needle = val.toLowerCase();
          roomRateTableOptions.value = roomRateTableList.value.filter(
            (v) => v.toLowerCase().indexOf(needle) > -1,
          );
        });
      },
    };
  },
};
</script>
